import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Search,
  FileText,
  Plus,
  Upload,
  Video,
  Camera,
  Clock,
} from "lucide-react";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../../../../components/ui/dialog";
import { Textarea } from "../../../../components/ui/textarea";
import { Label } from "../../../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../../../components/ui/tabs";
import { useAudit } from "../../../../contexts/auditContext";
import { controlsV2SessionApiBase } from "../../../../lib/sessionApiBase";
import { fetchFastApi } from "../../../../api/fastapi";
import { toast } from "sonner";
import { debounce } from "lodash";
import {
  type AuditSession,
  type FileType,
} from "../../../../models/audit/auditSession";
import { z } from "zod";
import FileViewer from "../../../extraction/viewExtraction/FileViewer";
import { type LinkingData } from "../../../extraction/viewExtraction/PdfViewer";
import { LoadingSpinner } from "../../../../components/ui/loadingSpinner/loadingSpinner";
import type {
  AuditorStep,
  ReperformanceStep,
} from "../../../../models/audit/attributeTestColumn";
import { pdfDocument } from "../../../../models/extraction";
import { CitationFileDropzone } from "./CitationFileDropzone";
import { getGroupLetter } from "../../../../lib/groupLabels";
import { isVideoFileExtension } from "../../../../lib/fileTypes";
import { usePresignedUpload } from "../../../../hooks/usePresignedUpload";
import { shouldUsePresignedUpload } from "../../../../api/presignedUpload";

interface SearchResultDocument {
  id: string;
  fileName: string | null;
  fileType: string | null;
  state: string;
  highlights: string[];
  sourceType: "procedure_session" | "supplementary";
  procedureSessionId: string | null;
}

interface PendingCitationData {
  linkingData: LinkingData;
  documentId: string;
  fileName: string | null;
  pageWidth?: number;
  pageHeight?: number;
}

interface UploadPendingCitationData {
  file: File;
  linkingData?: LinkingData;
  pageWidth?: number;
  pageHeight?: number;
  timestampSeconds?: number;
  uploadId?: string;
}

export interface UploadCitationResponse {
  citation: {
    id: string;
    documentId: string;
    encodedCitationString: string;
    shortSummary: string | null;
    boundingBox:
      | [[number, number], [number, number], [number, number], [number, number]]
      | null;
    pageNumber: number | null;
    pageWidth: number | null;
    pageHeight: number | null;
    citationOrder: number | null;
    reperformanceStepId: string | null;
    citationGroupId: string | null;
    auditorStepId: string | null;
    timestampSeconds: number | null;
    isKey: boolean | null;
    createdAt: string;
    updatedAt: string;
  };
  document: {
    id: string;
    fileName: string;
    fileType: FileType;
    fileUrl: string;
    fileContentHash: string;
    state: string;
    extra: Record<string, unknown>;
    parentDocumentId: string | null;
    childType: string | null;
    isParsed: boolean;
    createdAt: string;
    updatedAt: string;
  };
  sessionDocument?: {
    id: string;
    documentId: string;
    state: string;
    documentLevel: string;
    creatorType: string;
    createdAt: string;
    updatedAt: string;
  };
}

interface AddCitationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  auditSession: AuditSession;
  isAttributeTestColumn: boolean;
  reperformanceSteps: ReperformanceStep[];
  evalResultId?: string;
  /** When provided, the modal is in auditor-trail mode: the new citation
   * attaches to one of these L2 auditor steps (chosen via the in-dialog step
   * picker). The reperformance step + L2-group pickers are hidden — trail
   * citations carry no `citation_group_id`. */
  auditorSteps?: AuditorStep[];
  onCreateCitation: (data: {
    summary: string;
    reperformanceStepId: string | null;
    linkingData: LinkingData;
    documentId: string;
    pageWidth?: number;
    pageHeight?: number;
    isKey?: boolean;
    citationGroupId?: string | null;
    auditorStepId?: string | null;
  }) => Promise<void>;
  onUploadCitationCreated?: (response: UploadCitationResponse) => void;
  onVideoDocumentSelect?: (
    document: AuditSession["documents"][number],
    signedUrl: string,
    reperformanceStepId: string | null,
  ) => void;
}

function getFileTypeFromName(fileName: string): FileType {
  const ext = fileName.toLowerCase().split(".").pop();
  return ext as FileType;
}

function formatTimestamp(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

export const AddCitationModal = ({
  open,
  onOpenChange,
  auditSession,
  isAttributeTestColumn,
  reperformanceSteps,
  evalResultId,
  auditorSteps,
  onCreateCitation,
  onUploadCitationCreated,
  onVideoDocumentSelect,
}: AddCitationModalProps) => {
  // Auditor-trail mode: the citation attaches to an L2 auditor step picked in
  // this dialog (not a reperformance step), so the reperformance step picker is
  // replaced by the L2 step picker and the L2 group picker is hidden entirely —
  // trail citations carry no group.
  const isAuditorTrail = auditorSteps !== undefined;
  const { fetchPresignedUrl, controlSession, sessionKind } = useAudit();
  const {
    state: presignedUploadState,
    uploadFiles: presignedUploadFiles,
    reset: presignedUploadReset,
  } = usePresignedUpload();

  // Tab state
  const [activeTab, setActiveTab] = useState<"search" | "upload">("search");

  // Document search state
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<SearchResultDocument[]>(
    [],
  );
  const [isSearching, setIsSearching] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<{
    documentId: string;
    fileName: string | null;
    fileType?: FileType;
  } | null>(null);
  const [currentDocumentUrl, setCurrentDocumentUrl] = useState<string | null>(
    null,
  );
  const [documentExtraction, setDocumentExtraction] = useState<
    z.infer<typeof pdfDocument> | undefined
  >(undefined);

  // Upload state
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadedFileUrl, setUploadedFileUrl] = useState<string | null>(null);
  const [uploadedFileType, setUploadedFileType] = useState<FileType | null>(
    null,
  );
  const [uploadImageDimensions, setUploadImageDimensions] = useState<{
    width: number;
    height: number;
  } | null>(null);

  // Video upload state
  const [uploadVideoTimestamp, setUploadVideoTimestamp] = useState<
    number | null
  >(null);
  const uploadVideoRef = useRef<HTMLVideoElement>(null);

  // Citation details state
  const [pendingCitationData, setPendingCitationData] =
    useState<PendingCitationData | null>(null);
  const [uploadPendingCitationData, setUploadPendingCitationData] =
    useState<UploadPendingCitationData | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [summary, setSummary] = useState("");
  const [selectedStepId, setSelectedStepId] = useState<string>("");
  // Auditor-trail mode: the L2 auditor step the new citation attaches to.
  const [selectedAuditorStepId, setSelectedAuditorStepId] =
    useState<string>("");
  // Required citation_group (L2 group) to attach the new citation to. Scoped to the
  // selected step. Only an existing group can be picked; creating a new group
  // inline is not supported. If the step has no groups, the user cannot submit
  // a citation under it yet.
  const [selectedGroupId, setSelectedGroupId] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset the group selection whenever the step changes — groups are scoped per
  // step, so a previously-valid group id would no longer belong here.
  useEffect(() => {
    setSelectedGroupId("");
  }, [selectedStepId]);

  // Available groups under the currently-selected step, used to populate the
  // L2 group picker in the details modal.
  const availableGroups = useMemo(() => {
    if (!selectedStepId) return [];
    const step = reperformanceSteps.find((s) => s.id === selectedStepId);
    return step?.citationGroups ?? [];
  }, [reperformanceSteps, selectedStepId]);

  // On attribute-test columns the L2 group picker is required: once a step is
  // selected, the user must pick a group before submission is allowed. Centralised
  // here so the two submit handlers and the submit-button disabled prop stay in sync.
  // Auditor-trail citations carry no group, so the requirement never applies there.
  const groupRequiredButMissing =
    isAttributeTestColumn &&
    !isAuditorTrail &&
    !!selectedStepId &&
    !selectedGroupId;

  // Cleanup object URL when component unmounts or file changes
  useEffect(() => {
    return () => {
      if (uploadedFileUrl) {
        URL.revokeObjectURL(uploadedFileUrl);
      }
    };
  }, [uploadedFileUrl]);

  // Handle file selection for upload tab
  const handleFileSelect = useCallback((file: File) => {
    // Revoke previous URL if exists
    setUploadedFileUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });

    const url = URL.createObjectURL(file);
    setUploadedFile(file);
    setUploadedFileUrl(url);
    setUploadedFileType(getFileTypeFromName(file.name));
    setUploadImageDimensions(null);
    setUploadVideoTimestamp(null);

    const fileType = getFileTypeFromName(file.name);
    const ext = file.name.toLowerCase().split(".").pop() || "";

    // Skip dimension loading for PDFs and videos
    if (fileType !== "pdf" && !isVideoFileExtension(ext)) {
      const img = new window.Image();
      img.onload = () => {
        setUploadImageDimensions({
          width: img.naturalWidth,
          height: img.naturalHeight,
        });
      };
      img.src = url;
    }
  }, []);

  // Clear uploaded file
  const handleClearFile = useCallback(() => {
    if (uploadedFileUrl) {
      URL.revokeObjectURL(uploadedFileUrl);
    }
    setUploadedFile(null);
    setUploadedFileUrl(null);
    setUploadedFileType(null);
    setUploadImageDimensions(null);
    setUploadVideoTimestamp(null);
    presignedUploadReset();
  }, [uploadedFileUrl, presignedUploadReset]);

  // Debounced search using the new endpoint
  const debouncedSearch = useMemo(
    () =>
      debounce(async (term: string) => {
        if (!controlSession?.id) {
          setSearchResults([]);
          return;
        }

        setIsSearching(true);
        try {
          const response = await fetchFastApi({
            method: "GET",
            path: `${controlsV2SessionApiBase(sessionKind, controlSession.id)}/documents/search`,
            query: { query: term },
          });

          // Filter to PDFs (and videos if onVideoDocumentSelect is provided)
          const filteredDocs = (response.documents || []).filter(
            (doc: SearchResultDocument) => {
              if (doc.fileType?.toLowerCase() === "pdf") return true;
              if (doc.fileName?.toLowerCase().endsWith(".pdf")) return true;
              if (onVideoDocumentSelect) {
                const ext = doc.fileName?.split(".").pop()?.toLowerCase() || "";
                if (isVideoFileExtension(ext)) return true;
                if (doc.fileType && isVideoFileExtension(doc.fileType))
                  return true;
              }
              return false;
            },
          );
          setSearchResults(filteredDocs);
        } catch (error) {
          console.error("Error searching documents:", error);
          toast.error("Failed to search documents");
          setSearchResults([]);
        } finally {
          setIsSearching(false);
        }
      }, 300),
    [controlSession?.id, sessionKind, onVideoDocumentSelect],
  );

  // Trigger search when search term changes
  useEffect(() => {
    if (open && activeTab === "search") {
      debouncedSearch(searchTerm);
    }
    return () => debouncedSearch.cancel();
  }, [searchTerm, debouncedSearch, open, activeTab]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setActiveTab("search");
      setSearchTerm("");
      setSearchResults([]);
      setSelectedDocument(null);
      setCurrentDocumentUrl(null);
      setDocumentExtraction(undefined);
      setPendingCitationData(null);
      setUploadPendingCitationData(null);
      setShowDetailsModal(false);
      setSummary("");
      setSelectedStepId("");
      setSelectedGroupId("");
      setSelectedAuditorStepId("");
      setUploadVideoTimestamp(null);
      handleClearFile();
    }
  }, [open, handleClearFile]);

  // Reset opposite tab state when switching tabs
  const handleTabChange = useCallback(
    (value: string) => {
      const newTab = value as "search" | "upload";
      setActiveTab(newTab);

      if (newTab === "search") {
        // Clear upload state
        handleClearFile();
        setUploadVideoTimestamp(null);
      } else {
        // Clear search state
        setSelectedDocument(null);
        setCurrentDocumentUrl(null);
        setDocumentExtraction(undefined);
      }
    },
    [handleClearFile],
  );

  // Fetch presigned URL when document is selected
  useEffect(() => {
    const fetchUrl = async () => {
      if (!selectedDocument?.documentId) {
        setCurrentDocumentUrl(null);
        setDocumentExtraction(undefined);
        return;
      }

      const document = auditSession?.documents.find(
        (doc) => doc.documentId === selectedDocument.documentId,
      );

      const presignedData = await fetchPresignedUrl(
        document?.id || selectedDocument.documentId,
        auditSession.id,
      );
      setCurrentDocumentUrl(presignedData?.url || null);
      setDocumentExtraction(presignedData?.documentExtraction);
    };

    fetchUrl();
  }, [auditSession, selectedDocument, fetchPresignedUrl]);

  // Handle linking complete for search tab
  const handleLinkingComplete = useCallback(
    (linkingData: LinkingData) => {
      if (!selectedDocument) return;

      // Prefer dimensions from linkingData (reported by PdfViewer),
      // fall back to documentExtraction for backward compatibility
      const widths = documentExtraction?.widths;
      const heights = documentExtraction?.heights;
      const pageWidth =
        linkingData.pageWidth ??
        widths?.[linkingData.pageNumber - 1] ??
        widths?.[0] ??
        undefined;
      const pageHeight =
        linkingData.pageHeight ??
        heights?.[linkingData.pageNumber - 1] ??
        heights?.[0] ??
        undefined;

      setPendingCitationData({
        linkingData,
        documentId: selectedDocument.documentId,
        fileName: selectedDocument.fileName,
        pageWidth,
        pageHeight,
      });
      setShowDetailsModal(true);
    },
    [selectedDocument, documentExtraction],
  );

  // Handle linking complete for upload tab
  const handleUploadLinkingComplete = useCallback(
    (linkingData: LinkingData) => {
      if (!uploadedFile) return;

      // Use dimensions from linkingData (reported by PdfViewer for PDFs),
      // fall back to image dimensions for images
      const pageWidth = linkingData.pageWidth ?? uploadImageDimensions?.width;
      const pageHeight =
        linkingData.pageHeight ?? uploadImageDimensions?.height;

      setUploadPendingCitationData({
        linkingData,
        file: uploadedFile,
        pageWidth,
        pageHeight,
      });
      setShowDetailsModal(true);
    },
    [uploadedFile, uploadImageDimensions],
  );

  const handleSubmit = async () => {
    // Handle search tab submission
    if (activeTab === "search" && pendingCitationData) {
      if (!summary.trim()) return;

      // Auditor-trail mode requires an L2 step (picked in this dialog); the
      // reperformance step / group checks are skipped.
      if (isAuditorTrail) {
        if (!selectedAuditorStepId) {
          toast.error("Please select a step");
          return;
        }
      } else {
        // For attribute test columns, a reperformance step is required
        if (
          isAttributeTestColumn &&
          reperformanceSteps.length > 0 &&
          !selectedStepId
        ) {
          toast.error("Please select a step");
          return;
        }

        // If attribute test column has no steps, we can't add citations
        if (isAttributeTestColumn && reperformanceSteps.length === 0) {
          toast.error("No steps available to associate with citation");
          return;
        }

        // L2 group is required on attribute tests; see groupRequiredButMissing.
        if (groupRequiredButMissing) {
          toast.error("Please select a Group");
          return;
        }
      }

      setIsSubmitting(true);
      try {
        await onCreateCitation({
          summary: summary.trim(),
          reperformanceStepId:
            isAttributeTestColumn && !isAuditorTrail ? selectedStepId : null,
          linkingData: pendingCitationData.linkingData,
          documentId: pendingCitationData.documentId,
          pageWidth: pendingCitationData.pageWidth,
          pageHeight: pendingCitationData.pageHeight,
          isKey: true,
          citationGroupId: isAuditorTrail ? null : selectedGroupId || null,
          auditorStepId: isAuditorTrail ? selectedAuditorStepId || null : null,
        });

        toast.success("Citation added successfully");
        onOpenChange(false);
      } catch (error) {
        console.error("Error creating citation:", error);
        toast.error("Failed to add citation");
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    // Handle upload tab submission
    if (activeTab === "upload" && uploadPendingCitationData) {
      if (!summary.trim()) return;

      // Auditor-trail mode requires an L2 step (picked in this dialog); the
      // reperformance step / group checks are skipped.
      if (isAuditorTrail) {
        if (!selectedAuditorStepId) {
          toast.error("Please select a step");
          return;
        }
      } else {
        // For attribute test columns, a reperformance step is required
        if (
          isAttributeTestColumn &&
          reperformanceSteps.length > 0 &&
          !selectedStepId
        ) {
          toast.error("Please select a step");
          return;
        }

        if (isAttributeTestColumn && reperformanceSteps.length === 0) {
          toast.error("No steps available to associate with citation");
          return;
        }

        if (groupRequiredButMissing) {
          toast.error("Please select a Group");
          return;
        }
      }

      setIsSubmitting(true);
      try {
        const formData = new FormData();

        if (uploadPendingCitationData.timestampSeconds != null) {
          // Video path
          if (uploadPendingCitationData.uploadId) {
            formData.append("upload_id", uploadPendingCitationData.uploadId);
          } else {
            formData.append("file", uploadPendingCitationData.file);
          }
          formData.append(
            "timestamp_seconds",
            String(uploadPendingCitationData.timestampSeconds),
          );
        } else if (uploadPendingCitationData.linkingData) {
          // PDF/image path
          formData.append("file", uploadPendingCitationData.file);
          formData.append(
            "bounding_box",
            JSON.stringify(uploadPendingCitationData.linkingData.boundingBox),
          );
          formData.append(
            "page_number",
            String(uploadPendingCitationData.linkingData.pageNumber),
          );

          if (uploadPendingCitationData.pageWidth) {
            formData.append(
              "page_width",
              String(uploadPendingCitationData.pageWidth),
            );
          }
          if (uploadPendingCitationData.pageHeight) {
            formData.append(
              "page_height",
              String(uploadPendingCitationData.pageHeight),
            );
          }
        }

        formData.append("short_summary", summary.trim());
        // New citations default to key — user can unstar from the viewer.
        formData.append("is_key", "true");

        // Add context IDs. Auditor-trail citations attach to the chosen L2
        // `auditor_step` (no reperformance step, no L2 group); otherwise fall
        // back to the reperformance-step / eval-result parents and optional group.
        if (isAuditorTrail && selectedAuditorStepId) {
          formData.append("auditor_step_id", selectedAuditorStepId);
        } else if (isAttributeTestColumn && selectedStepId) {
          formData.append("reperformance_step_id", selectedStepId);
        } else if (evalResultId) {
          formData.append("eval_result_id", evalResultId);
        }

        if (!isAuditorTrail && selectedGroupId) {
          formData.append("citation_group_id", selectedGroupId);
        }

        const response = (await fetchFastApi({
          method: "POST",
          path: "/citations/upload",
          body: formData,
        })) as UploadCitationResponse;

        onUploadCitationCreated?.(response);
        toast.success("Citation uploaded successfully");
        onOpenChange(false);
      } catch (error) {
        console.error("Error uploading citation:", error);
        toast.error("Failed to upload citation");
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleDetailsCancel = () => {
    setShowDetailsModal(false);
    setPendingCitationData(null);
    setUploadPendingCitationData(null);
    setSummary("");
    setSelectedStepId("");
    setSelectedGroupId("");
    setSelectedAuditorStepId("");
  };

  // Get file type from document
  const getFileType = (fileName: string | null): FileType => {
    if (!fileName) return "pdf";
    const ext = fileName.split(".").pop()?.toLowerCase();
    if (ext === "xlsx" || ext === "xlsm" || ext === "xls") return "xlsx";
    if (ext === "csv") return "csv";
    if (ext === "mp4") return "mp4";
    if (ext === "mov") return "mov";
    return "pdf";
  };

  // Check if the selected search document is a video
  const isSelectedDocVideo = useMemo(() => {
    if (!selectedDocument?.fileType) return false;
    return isVideoFileExtension(selectedDocument.fileType);
  }, [selectedDocument?.fileType]);

  // Check if the uploaded file is a video
  const isUploadedFileVideo = useMemo(() => {
    if (!uploadedFileType) return false;
    return isVideoFileExtension(uploadedFileType);
  }, [uploadedFileType]);

  // Determine the pending data source
  const currentPendingFileName =
    activeTab === "search"
      ? pendingCitationData?.fileName
      : uploadPendingCitationData?.file.name;

  return (
    <>
      {/* Document Search/Upload Modal */}
      <Dialog open={open && !showDetailsModal} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-[1100px] h-[85vh] flex flex-col p-0 gap-0">
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-stone-200 shrink-0">
            <DialogTitle className="flex items-center gap-2 text-lg">
              <Plus className="h-5 w-5 text-stone-600" />
              Add Citation
            </DialogTitle>
            <DialogDescription className="text-sm text-stone-500">
              {activeTab === "search"
                ? "Search for a document, then draw a box around the area you want to cite"
                : "Upload a file, then draw a box or select a video timestamp to create a citation"}
            </DialogDescription>
          </DialogHeader>

          <Tabs
            value={activeTab}
            onValueChange={handleTabChange}
            className="flex-1 flex flex-col min-h-0"
          >
            <div className="px-2 shrink-0 my-2">
              <TabsList className="">
                <TabsTrigger value="search" className="flex items-center gap-2">
                  <Search className="h-4 w-4" />
                  Search From Existing Documents
                </TabsTrigger>
                <TabsTrigger value="upload" className="flex items-center gap-2">
                  <Upload className="h-4 w-4" />
                  Upload New Document
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent
              value="search"
              className="flex-1 flex min-h-0 overflow-hidden mt-0 data-[state=inactive]:hidden"
            >
              <div className="flex-1 flex min-h-0 overflow-hidden">
                {/* Left panel - Document list */}
                <div className="w-[340px] border-r border-stone-200 flex flex-col shrink-0">
                  <div className="px-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
                      <Input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search documents..."
                        className="pl-9"
                      />
                    </div>
                  </div>

                  <div className="flex-1 overflow-y-auto">
                    {isSearching ? (
                      <div className="flex items-center justify-center h-32">
                        <LoadingSpinner />
                      </div>
                    ) : searchResults.length > 0 ? (
                      <div className="p-2 space-y-1">
                        {searchResults.map((doc) => (
                          <button
                            key={doc.id}
                            type="button"
                            className={`w-full text-left p-3 transition-colors cursor-pointer ${
                              selectedDocument?.documentId === doc.id
                                ? "bg-stone-100 ring-1 ring-stone-300"
                                : "hover:bg-stone-50"
                            }`}
                            onClick={() => {
                              setCurrentDocumentUrl(null);
                              setSelectedDocument({
                                documentId: doc.id,
                                fileName: doc.fileName,
                                fileType: getFileType(doc.fileName),
                              });
                            }}
                          >
                            <div className="flex items-start gap-2">
                              {isVideoFileExtension(
                                doc.fileName?.split(".").pop()?.toLowerCase() ||
                                  "",
                              ) ? (
                                <Video className="h-4 w-4 text-stone-400 mt-0.5 shrink-0" />
                              ) : (
                                <FileText className="h-4 w-4 text-stone-400 mt-0.5 shrink-0" />
                              )}
                              <div className="min-w-0 flex-1">
                                <div className="text-sm font-medium text-stone-900 truncate">
                                  {doc.fileName || "Unnamed Document"}
                                </div>
                                {doc.highlights &&
                                  doc.highlights.length > 0 && (
                                    <div className="mt-1 space-y-1">
                                      {doc.highlights
                                        .slice(0, 2)
                                        .map((highlight, i) => (
                                          <div
                                            key={i}
                                            className="text-xs text-stone-500 line-clamp-2 break-words [&_mark]:bg-amber-100 [&_mark]:text-amber-900 [&_mark]:rounded-sm [&_mark]:px-0.5"
                                            dangerouslySetInnerHTML={{
                                              __html: highlight,
                                            }}
                                          />
                                        ))}
                                      {doc.highlights.length > 2 && (
                                        <div className="text-xs text-stone-400">
                                          +{doc.highlights.length - 2} more
                                          matches
                                        </div>
                                      )}
                                    </div>
                                  )}
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    ) : searchTerm ? (
                      <div className="flex flex-col items-center justify-center h-32 text-stone-500">
                        <p className="text-sm">No documents found</p>
                        <p className="text-xs mt-1">
                          Try a different search term
                        </p>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center h-32 text-stone-500">
                        <p className="text-sm">
                          Enter a search term to find documents
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Right panel - Document viewer */}
                <div className="flex-1 flex flex-col min-w-0 min-h-0">
                  {selectedDocument &&
                  currentDocumentUrl &&
                  isSelectedDocVideo ? (
                    <div className="flex-1 min-h-0 p-4 flex flex-col">
                      <div className="flex-1 bg-black rounded-lg overflow-hidden mb-4">
                        <video
                          src={currentDocumentUrl}
                          controls
                          className="w-full h-full object-contain"
                        >
                          <track kind="captions" />
                        </video>
                      </div>
                      {isAttributeTestColumn &&
                        !isAuditorTrail &&
                        reperformanceSteps.length > 0 && (
                          <Select
                            value={selectedStepId}
                            onValueChange={setSelectedStepId}
                          >
                            <SelectTrigger className="w-full h-auto min-h-10 items-start py-2 [&>span]:block [&>span]:whitespace-normal [&>span]:text-left [&>span]:max-w-[calc(100%-24px)]">
                              <SelectValue placeholder="Select a step..." />
                            </SelectTrigger>
                            <SelectContent className="!max-w-(--radix-select-trigger-width)">
                              {reperformanceSteps.map((step) => (
                                <SelectItem
                                  key={step.id}
                                  value={step.id || ""}
                                  className="items-start whitespace-normal [&>span:last-child]:whitespace-normal [&>span:last-child]:min-w-0"
                                >
                                  {step.stepName}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      <Button
                        className="w-full"
                        onClick={() => {
                          if (!selectedDocument || !currentDocumentUrl) return;
                          const sessionDoc = auditSession.documents.find(
                            (d) => d.documentId === selectedDocument.documentId,
                          );
                          if (sessionDoc && onVideoDocumentSelect) {
                            onVideoDocumentSelect(
                              sessionDoc,
                              currentDocumentUrl,
                              isAttributeTestColumn ? selectedStepId : null,
                            );
                            onOpenChange(false);
                          }
                        }}
                        disabled={
                          !onVideoDocumentSelect ||
                          (isAttributeTestColumn &&
                            !isAuditorTrail &&
                            reperformanceSteps.length > 0 &&
                            !selectedStepId)
                        }
                      >
                        <Camera className="h-4 w-4 mr-2" />
                        Select &amp; Create Video Citation
                      </Button>
                    </div>
                  ) : selectedDocument && currentDocumentUrl ? (
                    <div className="flex-1 min-h-0 p-4">
                      <div className="h-full w-full bg-white rounded-md border border-stone-200 overflow-hidden">
                        <FileViewer
                          signedUrl={currentDocumentUrl}
                          fileType={selectedDocument.fileType || "pdf"}
                          fileName={selectedDocument.fileName || undefined}
                          allowLinking={true}
                          onLinkingComplete={handleLinkingComplete}
                          documentExtraction={documentExtraction}
                        />
                      </div>
                    </div>
                  ) : selectedDocument ? (
                    <div className="flex-1 flex items-center justify-center">
                      <LoadingSpinner />
                    </div>
                  ) : (
                    <div className="flex-1 flex flex-col items-center justify-center ">
                      <FileText className="h-12 w-12 mb-3 stroke-1" />
                      <p className="text-sm">Select a document to preview</p>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            <TabsContent
              value="upload"
              className="flex-1 flex min-h-0 overflow-hidden mt-0 data-[state=inactive]:hidden"
            >
              <div className="flex-1 flex min-h-0 overflow-hidden">
                {/* Left panel - File dropzone */}
                <div className="w-[340px] border-r border-stone-200 flex flex-col shrink-0 p-4">
                  <CitationFileDropzone
                    onFileSelect={handleFileSelect}
                    selectedFile={uploadedFile}
                    onClearFile={handleClearFile}
                  />
                </div>

                {/* Right panel - File viewer */}
                <div className="flex-1 flex flex-col min-w-0 min-h-0">
                  {uploadedFile && uploadedFileUrl && isUploadedFileVideo ? (
                    <div className="flex-1 min-h-0 p-4 flex flex-col">
                      <div className="flex-1 bg-black rounded-lg overflow-hidden mb-4">
                        <video
                          ref={uploadVideoRef}
                          src={uploadedFileUrl}
                          controls
                          className="w-full h-full object-contain"
                          onTimeUpdate={() => {
                            if (uploadVideoRef.current) {
                              setUploadVideoTimestamp(
                                uploadVideoRef.current.currentTime,
                              );
                            }
                          }}
                        >
                          <track kind="captions" />
                        </video>
                      </div>

                      <div className="space-y-3">
                        <div className="flex items-center gap-3 p-3 bg-stone-50 rounded-lg border">
                          <Clock className="h-5 w-5 text-stone-500" />
                          <div>
                            <div className="text-xs text-stone-500">
                              Current Position
                            </div>
                            <div className="font-mono text-lg font-semibold">
                              {formatTimestamp(uploadVideoTimestamp ?? 0)}
                            </div>
                          </div>
                        </div>

                        {presignedUploadState.isUploading && (
                          <div className="p-3 bg-stone-50 rounded-lg border">
                            <div className="text-xs text-stone-500 mb-1">
                              Uploading video...
                            </div>
                            <div className="w-full bg-stone-200 rounded-full h-2">
                              <div
                                className="bg-[#ef3608] h-2 rounded-full transition-all"
                                style={{
                                  width: `${presignedUploadState.overallProgress}%`,
                                }}
                              />
                            </div>
                          </div>
                        )}

                        <Button
                          className="w-full"
                          disabled={presignedUploadState.isUploading}
                          onClick={async () => {
                            const timestamp = uploadVideoTimestamp ?? 0;

                            let uploadId: string | undefined;

                            if (shouldUsePresignedUpload([uploadedFile])) {
                              try {
                                const result = await presignedUploadFiles([
                                  uploadedFile,
                                ]);
                                if (result.usedPresigned) {
                                  uploadId = result.uploadIds[0];
                                }
                              } catch {
                                return;
                              }
                            }

                            setUploadPendingCitationData({
                              file: uploadedFile,
                              timestampSeconds: timestamp,
                              uploadId,
                            });
                            setShowDetailsModal(true);
                          }}
                        >
                          <Camera className="h-4 w-4 mr-2" />
                          Grab Frame at{" "}
                          {formatTimestamp(uploadVideoTimestamp ?? 0)}
                        </Button>
                      </div>
                    </div>
                  ) : uploadedFile && uploadedFileUrl ? (
                    <div className="flex-1 min-h-0 p-4">
                      <div className="h-full w-full bg-white rounded-md border border-stone-200 overflow-hidden">
                        <FileViewer
                          signedUrl={uploadedFileUrl}
                          fileType={uploadedFileType || "pdf"}
                          fileName={uploadedFile.name}
                          allowLinking={true}
                          onLinkingComplete={handleUploadLinkingComplete}
                          pageWidth={uploadImageDimensions?.width}
                          pageHeight={uploadImageDimensions?.height}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="flex-1 flex flex-col items-center justify-center text-stone-400">
                      <Upload className="h-12 w-12 mb-3 stroke-1" />
                      <p className="text-sm">Upload a file to preview</p>
                      <p className="text-xs mt-1 text-stone-400">
                        Then draw a box to create a citation
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Citation Details Modal */}
      <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Citation Details</DialogTitle>
            <DialogDescription className="text-sm text-stone-500">
              Add a summary for this citation
              {currentPendingFileName && (
                <span className="block mt-1 text-stone-600 font-medium truncate">
                  {currentPendingFileName}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="summary">
                Short Summary <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="summary"
                placeholder="Describe what this citation shows..."
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                rows={3}
                className="resize-none"
              />
            </div>

            {isAuditorTrail && (auditorSteps?.length ?? 0) > 0 && (
              <div className="space-y-2">
                <Label htmlFor="auditor-step">
                  Step <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={selectedAuditorStepId}
                  onValueChange={setSelectedAuditorStepId}
                >
                  <SelectTrigger
                    id="auditor-step"
                    className="w-full h-auto min-h-10 items-start py-2 [&>span]:block [&>span]:whitespace-normal [&>span]:text-left [&>span]:max-w-[calc(100%-24px)]"
                  >
                    <SelectValue placeholder="Select a step..." />
                  </SelectTrigger>
                  <SelectContent className="!max-w-(--radix-select-trigger-width)">
                    {(auditorSteps ?? []).map((step, idx) => (
                      <SelectItem
                        key={step.id}
                        value={step.id}
                        className="items-start whitespace-normal [&>span:last-child]:whitespace-normal [&>span:last-child]:min-w-0"
                      >
                        {idx + 1}. {step.text}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {isAuditorTrail && (auditorSteps?.length ?? 0) === 0 && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-800">
                No steps available to associate with this citation.
              </div>
            )}

            {isAttributeTestColumn &&
              !isAuditorTrail &&
              reperformanceSteps.length > 0 && (
                <div className="space-y-2">
                  <Label htmlFor="step">
                    Step <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={selectedStepId}
                    onValueChange={setSelectedStepId}
                  >
                    <SelectTrigger
                      id="step"
                      className="w-full h-auto min-h-10 items-start py-2 [&>span]:block [&>span]:whitespace-normal [&>span]:text-left [&>span]:max-w-[calc(100%-24px)]"
                    >
                      <SelectValue placeholder="Select a step..." />
                    </SelectTrigger>
                    <SelectContent className="!max-w-(--radix-select-trigger-width)">
                      {reperformanceSteps.map((step) => (
                        <SelectItem
                          key={step.id}
                          value={step.id || ""}
                          className="items-start whitespace-normal [&>span:last-child]:whitespace-normal [&>span:last-child]:min-w-0"
                        >
                          {step.stepName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

            {isAttributeTestColumn &&
              !isAuditorTrail &&
              reperformanceSteps.length === 0 && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-800">
                  No steps available. Citations for attribute test columns must
                  be associated with a step.
                </div>
              )}

            {/* L2 group is required. When the step has groups, show the picker;
                otherwise show a notice — a citation can't be added until a
                group exists, since groups can't be created inline here.
                Hidden entirely in auditor-trail mode (trail citations have no
                group). */}
            {isAttributeTestColumn &&
              !isAuditorTrail &&
              selectedStepId &&
              (availableGroups.length > 0 ? (
                <div className="space-y-2">
                  <Label htmlFor="group">
                    Group <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={selectedGroupId}
                    onValueChange={setSelectedGroupId}
                  >
                    <SelectTrigger
                      id="group"
                      className="w-full h-auto min-h-10 items-start py-2 [&>span]:block [&>span]:whitespace-normal [&>span]:text-left [&>span]:max-w-[calc(100%-24px)]"
                    >
                      <SelectValue placeholder="Select a Group..." />
                    </SelectTrigger>
                    <SelectContent className="!max-w-(--radix-select-trigger-width)">
                      {[...availableGroups]
                        .sort((a, b) => a.groupOrder - b.groupOrder)
                        .map((group, idx) => {
                          // Mirror the panel label: "<letter>: <short summary or
                          // group name>" so dropdown items line up with the L2
                          // group headings the user sees on the left. Reuse the
                          // panel's base-26 helper so letters match past 26 groups.
                          const letter = getGroupLetter(idx);
                          const label = group.shortSummary || group.groupName;
                          return (
                            <SelectItem
                              key={group.id}
                              value={group.id}
                              className="items-start whitespace-normal [&>span:last-child]:whitespace-normal [&>span:last-child]:min-w-0"
                            >
                              {letter}: {label}
                            </SelectItem>
                          );
                        })}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-800">
                  No Groups available on this step yet. Citations must attach to
                  an existing Group. Creating a new Group here is not supported
                  yet.
                </div>
              ))}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleDetailsCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={
                !summary.trim() ||
                (isAuditorTrail && !selectedAuditorStepId) ||
                (isAttributeTestColumn &&
                  !isAuditorTrail &&
                  reperformanceSteps.length === 0) ||
                (isAttributeTestColumn &&
                  !isAuditorTrail &&
                  reperformanceSteps.length > 0 &&
                  !selectedStepId) ||
                groupRequiredButMissing ||
                isSubmitting
              }
            >
              {isSubmitting ? "Adding..." : "Add Citation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
